export type NotificationPortal = 'apprenant' | 'formateur' | 'admin' | 'partenaire';

export type NotificationLinkInput = {
  deepLink?: string | null;
  payload?: Record<string, unknown> | null;
};

const SCREEN_PATHS: Record<NotificationPortal, Record<string, string>> = {
  apprenant: {
    documents: '/app/documents',
    'mes-cours': '/app/mes-cours',
    excellence: '/app/excellence',
    inscriptions: '/app/inscriptions',
    notes: '/app/notes',
    parrainage: '/app/parrainage',
    profil: '/app/profil',
    wallet: '/app/wallet',
  },
  formateur: {
    'mes-cours': '/formateur/sessions',
    sessions: '/formateur/sessions',
    evaluations: '/formateur/evaluations',
    excellence: '/formateur/performance',
    parrainage: '/formateur/parrainage',
    wallet: '/formateur/wallet',
  },
  admin: {
    documents: '/admin/documents',
    planning: '/admin/planning',
    communication: '/admin/communication',
    inscriptions: '/admin/inscriptions',
    dashboard: '/admin/dashboard',
    withdrawals: '/admin/withdrawals',
  },
  partenaire: {
    excellence: '/partenaire/parrainage',
    parrainage: '/partenaire/parrainage',
    wallet: '/partenaire/wallet',
  },
};

export function detectNotificationPortal(pathname: string): NotificationPortal | null {
  if (pathname.startsWith('/app')) return 'apprenant';
  if (pathname.startsWith('/formateur')) return 'formateur';
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/partenaire')) return 'partenaire';
  return null;
}

export function notificationInboxPath(portal: NotificationPortal): string {
  switch (portal) {
    case 'apprenant':
      return '/app/notifications';
    case 'formateur':
      return '/formateur/notifications';
    case 'admin':
      return '/admin/notifications';
    case 'partenaire':
      return '/partenaire/notifications';
    default:
      return '/app/notifications';
  }
}

export function resolveNotificationInboxPath(pathname: string): string | null {
  const portal = detectNotificationPortal(pathname);
  return portal ? notificationInboxPath(portal) : null;
}

function normalizeDeepLink(deepLink: string, portal: NotificationPortal): string | null {
  const trimmed = deepLink.trim();
  if (!trimmed) return null;

  if (
    trimmed.startsWith('/admin') ||
    trimmed.startsWith('/app') ||
    trimmed.startsWith('/formateur') ||
    trimmed.startsWith('/partenaire')
  ) {
    return trimmed;
  }

  if (trimmed === '/notifications') {
    return notificationInboxPath(portal);
  }

  const lower = trimmed.toLowerCase();
  if (lower.includes('document') || lower.includes('dossier')) {
    if (portal === 'admin') return '/admin/documents';
    if (portal === 'apprenant') return '/app/documents';
  }
  if (lower.includes('inscription')) {
    if (portal === 'admin') return '/admin/inscriptions';
    if (portal === 'apprenant') return '/app/inscriptions';
  }
  if (lower.includes('mes-cours') || lower.includes('session')) {
    if (portal === 'apprenant') return '/app/mes-cours';
    if (portal === 'formateur') return '/formateur/sessions';
  }
  if (lower.includes('note')) {
    if (portal === 'apprenant') return '/app/notes';
  }
  if (lower.includes('excellence') || lower.includes('stage')) {
    if (portal === 'apprenant') return '/app/excellence';
    if (portal === 'formateur') return '/formateur/performance';
    if (portal === 'partenaire') return '/partenaire/parrainage';
  }
  if (lower.includes('parrainage') || lower.includes('referral')) {
    if (portal === 'apprenant') return '/app/parrainage';
    if (portal === 'formateur') return '/formateur/parrainage';
    if (portal === 'partenaire') return '/partenaire/parrainage';
  }
  if (lower.includes('withdraw')) {
    if (portal === 'admin') return '/admin/withdrawals';
  }
  if (lower.includes('profile') || lower.includes('profil')) {
    if (portal === 'apprenant') return '/app/profil';
  }
  if (lower.includes('wallet') || lower.includes('portefeuille')) {
    if (portal === 'partenaire') return '/partenaire/wallet';
  }
  if (lower.includes('dashboard')) {
    if (portal === 'admin') return '/admin/dashboard';
  }

  return null;
}

/** Résout la destination web d'une notification pour le portail courant. */
export function resolveNotificationLink(
  row: NotificationLinkInput,
  portal: NotificationPortal,
): string | null {
  const payload = row.payload;
  const screen =
    payload && typeof payload === 'object' && typeof payload.screen === 'string'
      ? payload.screen
      : null;

  let basePath: string | null = null;
  if (screen && SCREEN_PATHS[portal][screen]) {
    basePath = SCREEN_PATHS[portal][screen];
  } else if (typeof row.deepLink === 'string') {
    basePath = normalizeDeepLink(row.deepLink, portal);
  }

  if (!basePath) return null;

  if (
    screen === 'documents' &&
    payload &&
    typeof payload.documentId === 'string' &&
    payload.documentId.trim()
  ) {
    return `${basePath}?document=${encodeURIComponent(payload.documentId.trim())}`;
  }

  return basePath;
}
